// src/ui/components/ActivityModal.js
// Controller for Activity Modal

import { getSupabaseClient } from "../../services/supabaseClient.js";
import { getCurrentTeamId, listTeamMembers } from "../../services/teamService.js";
import { createActivity, updateActivity, getActivityOutcomes } from "../../services/activitiesService.js";
import { actions } from "../../app/state.js";
import { formatDateEs } from "../../utils/dateUtils.js";

function $(root, sel) {
    return root.querySelector(sel);
}

function normStr(v) {
    return String(v ?? "").trim();
}

function normalizeSearchText(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
}

function escapeHtml(str) {
    return String(str ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}

function eventDateTokens(dateStr) {
    const iso = String(dateStr || "").slice(0, 10);
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return [iso].filter(Boolean);

    const [, year, month, day] = match;
    return [
        iso,
        `${day}/${month}/${year}`,
        `${day}-${month}-${year}`,
        `${day}/${month}`,
        `${day}-${month}`,
        `${month}/${year}`,
        `${month}-${year}`,
        year,
    ];
}

function eventStageLabel(stage) {
    switch (stage) {
        case "lead": return "Solicitud";
        case "info_needed": return "Info pendiente";
        case "proposal_sent": return "Presupuesto";
        case "negotiation": return "Negociacion";
        case "accepted_pending_deposit": return "Senal pendiente";
        case "confirmed": return "Confirmado";
        case "done": return "Realizado";
        case "rechazado": return "Rechazado";
        default: return stage || "";
    }
}

function eventDisplayName(event) {
    return event?.client_name_legacy || event?.name || "Evento sin nombre";
}

function eventPlaceLabel(event) {
    return event?.primary_venue_name || event?.location || event?.city || event?.region || "";
}

function eventSearchHaystack(event) {
    return normalizeSearchText([
        eventDisplayName(event),
        event?.type,
        event?.stage,
        eventStageLabel(event?.stage),
        eventPlaceLabel(event),
        event?.city,
        event?.region,
        ...eventDateTokens(event?.date),
    ].filter(Boolean).join(" "));
}

function setupActivityEventSearch(root, eventSelect, events, selectedEventId = null) {
    if (!eventSelect) return;

    const eventRows = (events || [])
        .slice()
        .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));

    eventSelect.innerHTML = '<option value="">— Selecciona un evento —</option>';
    eventRows.forEach(event => {
        const opt = document.createElement("option");
        opt.value = event.id;
        opt.textContent = `${eventDisplayName(event)} (${event.date ? formatDateEs(event.date) : "Sin fecha"})`;
        eventSelect.appendChild(opt);
    });
    eventSelect.value = selectedEventId || "";
    eventSelect.classList.add("hidden");

    const wrapper = document.createElement("div");
    wrapper.className = "relative";
    wrapper.innerHTML = `
        <div class="relative">
            <input id="activity-modal-event-search" type="text" autocomplete="off"
                class="w-full px-3 py-2 pl-9 rounded-xl border border-blue-200 bg-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                placeholder="Buscar por nombre, fecha, lugar..." />
            <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
        </div>
        <div id="activity-modal-event-results"
            class="hidden absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg custom-scrollbar"></div>
    `;
    eventSelect.after(wrapper);

    const input = $(root, "#activity-modal-event-search");
    const results = $(root, "#activity-modal-event-results");
    const selectedEvent = eventRows.find(event => event.id === selectedEventId);
    if (selectedEvent && input) input.value = `${eventDisplayName(selectedEvent)} (${selectedEvent.date ? formatDateEs(selectedEvent.date) : "Sin fecha"})`;

    function closeResults() {
        results?.classList.add("hidden");
    }

    function selectEvent(event) {
        eventSelect.value = event?.id || "";
        if (input) input.value = event ? `${eventDisplayName(event)} (${event.date ? formatDateEs(event.date) : "Sin fecha"})` : "";
        closeResults();
    }

    function renderResults(query = "") {
        if (!results) return;
        const normalizedQuery = normalizeSearchText(query);
        const matches = (normalizedQuery
            ? eventRows.filter(event => eventSearchHaystack(event).includes(normalizedQuery))
            : eventRows
        ).slice(0, 30);

        if (!matches.length) {
            results.innerHTML = `<div class="px-3 py-4 text-sm text-slate-500">No hay eventos para esa busqueda.</div>`;
        } else {
            results.innerHTML = matches.map(event => {
                const place = eventPlaceLabel(event);
                const meta = [event.date ? formatDateEs(event.date) : "Sin fecha", eventStageLabel(event.stage), place].filter(Boolean).join(" · ");
                return `
                    <button type="button" data-activity-event-id="${escapeHtml(event.id)}"
                        class="w-full px-3 py-2 text-left hover:bg-blue-50 border-b border-slate-100 last:border-b-0">
                        <span class="block text-sm font-bold text-slate-900 truncate">${escapeHtml(eventDisplayName(event))}</span>
                        <span class="block text-xs text-slate-500 truncate">${escapeHtml(meta)}</span>
                    </button>
                `;
            }).join("");
        }
        results.classList.remove("hidden");
    }

    input?.addEventListener("focus", () => renderResults(input.value));
    input?.addEventListener("input", () => {
        eventSelect.value = "";
        renderResults(input.value);
    });

    results?.addEventListener("mousedown", (ev) => {
        ev.preventDefault();
        const eventBtn = ev.target.closest("[data-activity-event-id]");
        if (!eventBtn) return;
        selectEvent(eventRows.find(event => event.id === eventBtn.getAttribute("data-activity-event-id")));
    });

    document.addEventListener("mousedown", (ev) => {
        if (!wrapper.contains(ev.target)) closeResults();
    });
}

async function requireSession() {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    if (!data?.user) throw new Error("No hay sesión activa.");
    return data.user;
}

function readForm(root) {
    return {
        type: normStr($(root, "#activity-type")?.value),
        scheduled_date: normStr($(root, "#activity-scheduled-date")?.value),
        subject: normStr($(root, "#activity-subject")?.value),
        description: normStr($(root, "#activity-description")?.value) || null,
        assigned_to: normStr($(root, "#activity-assigned-to")?.value) || null,
        status: $(root, "#activity-status")?.value || "pending",
        outcome: $(root, "#activity-outcome")?.value || null,
        outcome_notes: normStr($(root, "#activity-outcome-notes")?.value) || null,
    };
}

function validateForm(values, { productionMode = false } = {}) {
    const errors = [];

    if (!values.type) errors.push("Tipo de actividad es obligatorio.");
    if (!productionMode && !values.scheduled_date) errors.push("Fecha programada es obligatoria.");
    if (!values.subject) errors.push("Asunto es obligatorio.");
    if (!productionMode && values.status === "completed" && !values.outcome) {
        errors.push("Si la actividad está completada, debes seleccionar un resultado.");
    }

    return errors;
}

function showErrors(root, messages) {
    const box = $(root, "#activity-modal-errors");
    const list = $(root, "#activity-modal-errors-list");

    if (!messages.length) {
        box?.classList.add("hidden");
        return;
    }

    box?.classList.remove("hidden");
    if (list) {
        list.innerHTML = messages.map(m => `<li>${m}</li>`).join("");
    }
}

async function loadAndFillAssignees(root, teamId, { selectId = null } = {}) {
    const members = await listTeamMembers(teamId);
    const sel = $(root, "#activity-assigned-to");
    if (!sel) return members;

    // Sort: Admin first, then Manager, then Viewer, then alphabetic
    const rolePriority = { admin: 0, manager: 1, viewer: 2 };
    members.sort((a, b) => {
        const pa = rolePriority[a.role] ?? 9;
        const pb = rolePriority[b.role] ?? 9;
        if (pa !== pb) return pa - pb;
        return (a.display_name || "").localeCompare(b.display_name || "");
    });

    sel.innerHTML = "";
    const o0 = document.createElement("option");
    o0.value = "";
    o0.textContent = "— Sin asignar —";
    sel.appendChild(o0);

    for (const m of members) {
        const o = document.createElement("option");
        o.value = m.user_id;
        const roleLabels = { admin: 'Admin', manager: 'Manager', viewer: 'Colaborador' };
        const roleLabel = ` (${roleLabels[m.role] || m.role})`;
        o.textContent = (m.display_name || "Sin nombre") + roleLabel;
        sel.appendChild(o);
    }

    if (selectId) sel.value = selectId;

    return members;
}

async function saveActivity({ mode, activityId, eventId, teamId, root }) {
    const productionMode = root?.dataset?.productionTask === "true";
    const values = readForm(root);
    if (productionMode) {
        values.type = "task";
        if (values.status === "completed" && !values.outcome) values.outcome = "other";
    }
    const errors = validateForm(values, { productionMode });
    showErrors(root, errors);
    if (errors.length) return { ok: false };

    const payload = {
        event_id: eventId,
        team_id: teamId,
        type: values.type,
        subject: values.subject,
        description: values.description,
        scheduled_date: values.scheduled_date || null,
        assigned_to: values.assigned_to,
        status: values.status,
        outcome: values.status === 'completed' ? values.outcome : null,
        outcome_notes: values.status === 'completed' ? values.outcome_notes : null,
        completed_date: values.status === 'completed' ? new Date().toISOString() : null
    };

    let saved;
    if (mode === "create") {
        saved = await createActivity(payload);
    } else {
        saved = await updateActivity(activityId, payload);
    }

    return { ok: true, activity: saved };
}

function setupActivityModalUI(root, { status = "pending", outcome = "", isNew = false, productionMode = false } = {}) {
    const statusSel = $(root, "#activity-status");
    const outcomeGroup = $(root, "#activity-outcome-group");
    const outcomeSel = $(root, "#activity-outcome");
    const outcomeSelectWrap = outcomeSel?.closest("div");
    const outcomeNotes = $(root, "#activity-outcome-notes");
    const outcomeNotesWrap = outcomeNotes?.closest("div");
    const outcomeNotesLabel = root.querySelector('label[for="activity-outcome-notes"]');
    const completeBtn = $(root, "#activity-modal-complete");

    // Populate outcomes
    if (outcomeSel) {
        outcomeSel.innerHTML = '<option value="">— Selecciona —</option>';
        getActivityOutcomes().forEach(o => {
            const opt = document.createElement("option");
            opt.value = o.id;
            opt.textContent = o.label;
            outcomeSel.appendChild(opt);
        });
        if (outcome) outcomeSel.value = outcome;
        if (productionMode && !outcomeSel.value) outcomeSel.value = "other";
    }

    if (productionMode) {
        outcomeSelectWrap?.classList.add("hidden");
        outcomeNotesWrap?.classList.add("md:col-span-2");
        if (outcomeNotesLabel) outcomeNotesLabel.textContent = "Notas de cierre";
        if (outcomeNotes) {
            outcomeNotes.placeholder = "Ej: confirmado con venue / material listo / 17:30h";
        }
    }

    const toggleOutcome = (s) => {
        if (s === "completed") {
            outcomeGroup?.classList.remove("hidden");
            completeBtn?.classList.add("hidden");
            if (productionMode && outcomeSel && !outcomeSel.value) outcomeSel.value = "other";
        } else {
            outcomeGroup?.classList.add("hidden");
            if (s === "pending" && !isNew) completeBtn?.classList.remove("hidden");
            else completeBtn?.classList.add("hidden");
        }
    };

    if (statusSel) {
        statusSel.value = status;
        statusSel.onchange = () => toggleOutcome(statusSel.value);
    }

    toggleOutcome(status);

    // Complete Button logic
    if (completeBtn) {
        completeBtn.onclick = () => {
            if (statusSel) {
                statusSel.value = "completed";
                toggleOutcome("completed");
                if (productionMode) outcomeNotes?.focus();
                else outcomeSel?.focus();
                // We don't click save automatically because outcome is usually required
            }
        };
    }
}

function applyProductionTaskMode(root, activity) {
    root.dataset.productionTask = "true";

    const title = $(root, "#activity-modal-title");
    if (title) title.textContent = "Editar tarea de produccion";

    const type = $(root, "#activity-type");
    if (type) {
        type.value = "task";
        type.disabled = true;
        type.closest("div")?.classList.add("hidden");
    }

    const scheduled = $(root, "#activity-scheduled-date");
    if (scheduled) scheduled.required = false;

    const subject = $(root, "#activity-subject");
    if (subject) {
        subject.readOnly = true;
        subject.classList.add("bg-slate-50", "cursor-not-allowed");
    }

    const subjectLabel = root.querySelector('label[for="activity-subject"]');
    if (subjectLabel) subjectLabel.textContent = "Campo de produccion";

    const scheduledLabel = root.querySelector('label[for="activity-scheduled-date"]');
    if (scheduledLabel) scheduledLabel.textContent = "Fecha/hora interna";

    const assignedLabel = root.querySelector('label[for="activity-assigned-to"]');
    if (assignedLabel) assignedLabel.textContent = "Responsable interno";

    const descriptionLabel = root.querySelector('label[for="activity-description"]');
    if (descriptionLabel) descriptionLabel.textContent = "Dato operativo visible";

    const description = $(root, "#activity-description");
    if (description) {
        description.placeholder = "Ej: 17:30h, puerta de carga por calle Mayor, llevar DI extra...";
    }

    const saveBtn = $(root, "#activity-modal-save");
    if (saveBtn) saveBtn.innerHTML = '<i class="fa-solid fa-floppy-disk mr-2"></i>Guardar produccion';

    const eventInfoEl = $(root, "#activity-event-info");
    if (eventInfoEl) {
        eventInfoEl.insertAdjacentHTML(
            "afterbegin",
            '<span class="px-1.5 py-0.5 bg-emerald-50 rounded text-emerald-700 font-bold uppercase tracking-wider text-[9px]">Produccion</span>'
        );
    }
}

export async function openCreateActivityModal({ event_id, eventId, onSaved, assigned_to } = {}) {
    // Handle both event_id and eventId naming
    let finalEventId = event_id || eventId;

    const user = await requireSession();
    const teamId = await getCurrentTeamId({ profileOptions: { force: true, allowNull: false } });

    if (!window.CRM?.modals?.openModal) {
        actions.addToast?.({ type: "error", message: "Modal system no disponible." });
        return;
    }

    const modalEl = window.CRM.modals.openModal("activity-modal-template");
    const closeFn = () => window.CRM?.modals?.closeModal?.(modalEl);

    // Set header & Fetch Event Info
    const title = $(modalEl, "#activity-modal-title");
    const eventInfoEl = $(modalEl, "#activity-event-info");
    const eventSelectionContainer = $(modalEl, "#activity-modal-event-selection-container");
    const eventSelect = $(modalEl, "#activity-modal-event-id");

    if (title) title.textContent = "Nueva Actividad";

    if (finalEventId) {
        if (eventInfoEl) {
            eventInfoEl.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Cargando evento...';
            // Fetch event name for context
            getSupabaseClient()
                .from("events")
                .select("client_name_legacy, date")
                .eq("id", finalEventId)
                .single()
                .then(({ data }) => {
                    if (data) {
                        const dateStr = new Date(data.date).toLocaleDateString("es-ES", { day: 'numeric', month: 'short' });
                        eventInfoEl.innerHTML = `
                            <span class="px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 font-bold uppercase tracking-wider text-[9px]">Evento</span>
                            <span class="text-slate-900">${data.client_name_legacy}</span>
                            <span class="text-slate-300">•</span>
                            <span>${dateStr}</span>
                        `;
                    } else {
                        eventInfoEl.innerHTML = '<span class="text-red-400">Error cargando evento</span>';
                    }
                });
        }
    } else {
        // No event provided, show selector
        if (eventSelectionContainer && eventSelect) {
            eventSelectionContainer.classList.remove("hidden");
            try {
                const { listAllEvents } = await import("../../services/eventsService.js");
                const events = await listAllEvents();
                setupActivityEventSearch(modalEl, eventSelect, events);
            } catch (err) {
                console.error("Error cargando eventos", err);
            }
        }
    }

    // Default Date - Now + 1 hour, rounded to next 15 mins
    const now = new Date();
    now.setHours(now.getHours() + 1);
    now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15);
    now.setSeconds(0);
    now.setMilliseconds(0);

    const tzOffset = now.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(now - tzOffset)).toISOString().slice(0, 16);
    const dateInput = $(modalEl, "#activity-scheduled-date");
    if (dateInput) dateInput.value = localISOTime;

    // Load assignees (Managers first)
    await loadAndFillAssignees(modalEl, teamId, { selectId: assigned_to || user.id });

    // Setup Status & Outcome logic
    setupActivityModalUI(modalEl, { isNew: true });

    // Wire buttons
    $(modalEl, "#activity-modal-close")?.addEventListener("click", closeFn);
    $(modalEl, "#activity-modal-cancel")?.addEventListener("click", closeFn);

    $(modalEl, "#activity-modal-save")?.addEventListener("click", async () => {
        const btn = $(modalEl, "#activity-modal-save");
        const originalHtml = btn.innerHTML;

        // If no ID was passed initially, get it from the select
        const saveEventId = finalEventId || eventSelect?.value;

        if (!saveEventId) {
            actions.addToast?.({ type: "warning", message: "Debes seleccionar un evento." });
            return;
        }

        try {
            btn.disabled = true;
            btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin mr-2"></i>Guardando...';

            const res = await saveActivity({ mode: "create", eventId: saveEventId, teamId, root: modalEl });
            if (!res.ok) {
                btn.disabled = false;
                btn.innerHTML = originalHtml;
                return;
            }

            actions.addToast?.({ type: "success", message: "Actividad creada" });
            closeFn();
            onSaved?.(res.activity);
        } catch (e) {
            console.error(e);
            actions.addToast?.({ type: "error", message: String(e?.message || e) });
            btn.disabled = false;
            btn.innerHTML = originalHtml;
        }
    });

    showErrors(modalEl, []);
}

export async function openEditActivityModal(activityId, { onSaved } = {}) {
    if (!activityId) return;

    await requireSession();
    const teamId = await getCurrentTeamId({ profileOptions: { force: true, allowNull: false } });

    if (!window.CRM?.modals?.openModal) return;

    const modalEl = window.CRM.modals.openModal("activity-modal-template");
    const closeFn = () => window.CRM?.modals?.closeModal?.(modalEl);

    // Set header
    const title = $(modalEl, "#activity-modal-title");
    const eventInfoEl = $(modalEl, "#activity-event-info");
    if (title) title.textContent = "Editar Actividad";

    // Fetch Activity Data
    try {
        const supabase = getSupabaseClient();
        const { data: activity, error } = await supabase
            .from("activities")
            .select(`
                *,
                event:events(id, client_name_legacy, date)
            `)
            .eq("id", activityId)
            .single();

        if (error) throw error;

        // Fill Event Info
        if (eventInfoEl && activity.event) {
            const dateStr = new Date(activity.event.date).toLocaleDateString("es-ES", { day: 'numeric', month: 'short' });
            eventInfoEl.innerHTML = `
                <span class="px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 font-bold uppercase tracking-wider text-[9px]">Evento</span>
                <span class="text-slate-900">${activity.event.client_name_legacy}</span>
                <span class="text-slate-300">•</span>
                <span>${dateStr}</span>
            `;
        }

        // Fill Form
        $(modalEl, "#activity-type").value = activity.type;
        $(modalEl, "#activity-subject").value = activity.subject;
        $(modalEl, "#activity-description").value = activity.description || "";

        if (activity.scheduled_date) {
            const date = new Date(activity.scheduled_date);
            const tzOffset = date.getTimezoneOffset() * 60000;
            const localISO = (new Date(date - tzOffset)).toISOString().slice(0, 16);
            $(modalEl, "#activity-scheduled-date").value = localISO;
        }

        // Load assignees
        await loadAndFillAssignees(modalEl, teamId, { selectId: activity.assigned_to });

        const productionMode = activity.is_logistics_task === true;
        if (productionMode) applyProductionTaskMode(modalEl, activity);

        // Setup Status & Outcome logic
        setupActivityModalUI(modalEl, {
            status: activity.status,
            outcome: activity.outcome,
            isNew: false,
            productionMode
        });

        if (activity.outcome_notes) {
            $(modalEl, "#activity-outcome-notes").value = activity.outcome_notes;
        }

        // Wire Buttons
        $(modalEl, "#activity-modal-close")?.addEventListener("click", closeFn);
        $(modalEl, "#activity-modal-cancel")?.addEventListener("click", closeFn);

        $(modalEl, "#activity-modal-save")?.addEventListener("click", async () => {
            const btn = $(modalEl, "#activity-modal-save");
            const originalHtml = btn.innerHTML;

            try {
                btn.disabled = true;
                btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin mr-2"></i>Guardando...';

                const res = await saveActivity({ mode: "edit", activityId, eventId: activity.event_id, teamId, root: modalEl });
                if (!res.ok) {
                    btn.disabled = false;
                    btn.innerHTML = originalHtml;
                    return;
                }

                actions.addToast?.({ type: "success", message: "Actividad actualizada" });
                closeFn();
                onSaved?.(res.activity);
            } catch (e) {
                console.error(e);
                actions.addToast?.({ type: "error", message: String(e?.message || e) });
                btn.disabled = false;
                btn.innerHTML = originalHtml;
            }
        });

    } catch (e) {
        console.error(e);
        actions.addToast?.({ type: "error", message: "Error cargando actividad" });
        closeFn();
    }
}

export default {
    openCreateActivityModal,
    openEditActivityModal
};
